import {Typography, css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  utils,
  omitNullsy,
  enumContainer,
  useApiQuery,
  LightSplitLayout,
  getScrollOffsetBottom,
  LoadingSpinner,
  useLocationQuery,
  useCurrentLocale,
  ENABLE_PH_REC,
  RISKINESS_COLORS,
  DlMuiDynamicIcon,
  DateUtils,
  MaximizeButton,
  useMaximizeWrapper,
} from '@wallaby-ui/core';
import {
  DateFormatter,
  HospitalizationsLinkFormatter,
  HospitalizationsListDepartmentFormatter,
  JustTooltipFormatter,
  LightDateRangeFilter,
  LightNumberRangeFilter,
  LightSelectFilter,
  LightSelectedFilters,
  LightTextFilter,
  RiskFactorsFormatter,
  TranslateEnumFormatter,
  AG_GRID_COMPATIBILITY,
  useTableData,
  MaterialTableWrapper,
  DEFAULT_PAGE_SIZE,
  getDefaultMTOptions,
  useFormatters,
  getMultiFilter,
  useTableParams,
  MerieSchema,
  clampPageNumber,
  usePermissions,
  TableSorting,
  TablePaging,
  UserFilterDto,
  useUserFilters,
  Formatters,
  LightMedicationAtcFilter2,
  LightOrganizationFilter2,
  ChipsFormatter,
  getFollowUpDateIconTooltip,
  ShortcutEnum,
  useInactiveAtcRenderProps,
  LightSelectedFiltersPane,
  LightFilterPanel,
  LightFilterTabDef,
  SelectedFilter,
  countSelectedFilters,
  useOrganizations,
  useMedicationAtcs,
  useAtbList,
  useSnomedLabels,
  useMedications,
  formatLabMethodName,
  WallabyCoreSchema,
  LightSelectFilterVirtualized,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {isEqual, sortBy} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo, useRef} from 'react';
import {Helmet} from 'react-helmet';
import {defineMessages, useIntl} from 'react-intl';

import {
  applyBiochemistryRowChange,
  BIOCHEMISTRY_ROWS,
  BiochemistryRow,
  biochemistryParams,
  countBiochemistryItems,
  hasBlockingBiochemistryRow,
  isBiochemistryRowComplete,
  isBiochemistryRowStarted,
  packBiochemistryRows,
  removeBiochemistryRow,
  showBiochemistryRows,
} from './hospitalizedPatientsBiochemistry';
import {
  ACTIVE_INGREDIENT_SLOT_LABELS,
  ACTIVE_INGREDIENT_SLOT_NAMES,
  ATC_SLOT_LABELS,
  ATC_SLOT_NAMES,
  countMedicationConditions,
  MedicationSlotName,
  packMedicationSlots,
} from './hospitalizedPatientsMedication';
import {
  applyMicrobiologyRowChange,
  countMicrobiologyConditions,
  isMicrobiologyRowStarted,
  MICROBIOLOGY_ROWS,
  microbiologyParams,
  MicrobiologyPart,
  MicrobiologyRow,
  removeMicrobiologyRow,
  showMicrobiologyRows,
} from './hospitalizedPatientsMicrobiology';
import {formatIcdOptionLabel, otherParams} from './hospitalizedPatientsOther';
import {useHospitalizedPatientsExport} from './useHospitalizedPatientsExport';
import {useAppSelector} from 'src/js/store_hooks';

const I18N = defineMessages({
  CAPTION: {
    id: 'patient.list.caption',
    defaultMessage: 'Hospitalizations',
    description: 'caption for hospitalizations list component',
  },
  PAGE_TITLE: {
    id: 'patient.list.header.title',
    defaultMessage: 'Hospitalized patients',
    description: 'title for hospitalization list page',
  },
});
const PATIENTS_LAYOUT_SPLIT_CONFIG = [
  {
    sizes: [233, 767],
    minSize: [64, 100],
    layoutId: '/LightSplitLayout.HospitalizedPatientsLayout',
  },
];
type Props = {
  config: ConfigObjectType;
};

type PatientsFilters = {
  patientFullName?: string;
  patientIdentificationNumber?: string;
  patientSrcId?: string;
  lastEncounterSubOrganizationIds?: string[];
  encounterOrganizationIds?: string[];
  lastEncounterSubStartedFrom?: string;
  lastEncounterSubStartedTo?: string;
  lastEncounterSubFloatingShortcutName?: ShortcutEnum | null;
  encounterStartedFrom?: string;
  encounterStartedTo?: string;
  encounterFloatingShortcutName?: ShortcutEnum | null;
  hospitalizationStates?: string[];
  riskScoreFrom?: number;
  riskScoreTo?: number;
  riskFactorTypes?: string[];
  riskFactorsCountFrom?: number;
  riskFactorsCountTo?: number;
  commentSearchString?: string;
  // lastMedicationsCountFrom?: string;
  // lastMedicationsCountTo?: string;
  atcHierarchyIds1?: string[];
  atcHierarchyIds2?: string[];
  atcHierarchyIds3?: string[];
  activeIngredientIds1?: string[];
  activeIngredientIds2?: string[];
  activeIngredientIds3?: string[];
  atcHierarchyFrom?: string;
  atcHierarchyTo?: string;
  atcHierarchyFloatingShortcutName?: ShortcutEnum | null;
  atcHierarchyMatch?: string;
  biochemistryMethod1?: string;
  biochemistryOperator1?: string;
  biochemistryValue1?: string;
  biochemistryMethod2?: string;
  biochemistryOperator2?: string;
  biochemistryValue2?: string;
  biochemistryMethod3?: string;
  biochemistryOperator3?: string;
  biochemistryValue3?: string;
  biochemistryMethod4?: string;
  biochemistryOperator4?: string;
  biochemistryValue4?: string;
  biochemistryMethod5?: string;
  biochemistryOperator5?: string;
  biochemistryValue5?: string;
  biochemistryFrom?: string;
  biochemistryTo?: string;
  biochemistryFloatingShortcutName?: ShortcutEnum | null;
  biochemistryMatch?: string;
  pathogenCodes1?: string[];
  resistanceAtbCodes1?: string[];
  pathogenCodes2?: string[];
  resistanceAtbCodes2?: string[];
  pathogenCodes3?: string[];
  resistanceAtbCodes3?: string[];
  microbiologyFrom?: string;
  microbiologyTo?: string;
  microbiologyFloatingShortcutName?: ShortcutEnum | null;
  microbiologyMatch?: string;
  // Other tab (PDM-2749): structured ICD diagnoses, patient attributes as inclusive ranges, death options.
  icdCodes?: string[];
  ageFrom?: number;
  ageTo?: number;
  weightFrom?: number;
  weightTo?: number;
  bmiFrom?: number;
  bmiTo?: number;
  deathDuringHospitalizations?: string[];
  // filters before PhRec
  riskSeverities?: string[];
  evaluationStates?: string[];
  evaluationStateModifiedFrom?: string;
  evaluationStateModifiedTo?: string;
  // filters after PhRec
  pharmaRecommendationRiskinesses?: string[];
  pharmaRecommendationDateFrom?: string;
  pharmaRecommendationDateTo?: string;
  pharmaRecommendationFloatingShortcutName?: ShortcutEnum | null;
  pharmaRecommendationFollowUpDateFrom?: string;
  pharmaRecommendationFollowUpDateTo?: string;
  pharmaRecommendationFollowUpFloatingShortcutName?: ShortcutEnum | null;
};
/**
 * Filter-panel tabs (PDM-2750): General, Medication, Biochemistry, Microbiology (PDM-2748) and Other (PDM-2749). A
 * tab can still be defined but rendered with
 * display: none (`hidden` on the tab definition), so switching it on is one flag once the filters arrive.
 */
export type HospitalizationFilterTab = 'general' | 'medication' | 'laboratory' | 'microbiology' | 'other';
type FiltersData = {
  patientsOpen: boolean;
  /** Selected filter-panel tab; part of the page state so it survives a reload like the open flag does. */
  patientsTab: HospitalizationFilterTab;
  patients: PatientsFilters;
  patientsSorting: TableSorting;
  patientsPaging: TablePaging;
};
export type PatientFiltersData = FiltersData['patients'];
const DEFAULT_PATIENTS_OPEN = true;
const DEFAULT_PATIENTS_TAB: HospitalizationFilterTab = 'general';
const DEFAULT_PATIENTS_FILTERS: PatientsFilters = {
  hospitalizationStates: ['ACTIVE'],
  atcHierarchyMatch: 'ANY_DAY',
  // Same default as the medication box, so its selector shows "any day" instead of an empty field.
  biochemistryMatch: 'ANY_DAY',
  microbiologyMatch: 'ANY_DAY',
};
/**
 * The occurrence selects (medication, laboratory, microbiology) always show a value. The backend reads a missing
 * occurrence as "any day", and a filter arriving from a bookmark or from a saved filter written before the key
 * existed carries none - filled on the way in, so that the field, the chips and the saved-filter comparison all see
 * the same value instead of an empty select over a list that is in fact filtered "any day".
 */
function withOccurrenceDefaults<T extends Partial<PatientsFilters>>(patients: T): T {
  return {
    ...patients,
    atcHierarchyMatch: patients.atcHierarchyMatch ?? DEFAULT_PATIENTS_FILTERS.atcHierarchyMatch,
    biochemistryMatch: patients.biochemistryMatch ?? DEFAULT_PATIENTS_FILTERS.biochemistryMatch,
    microbiologyMatch: patients.microbiologyMatch ?? DEFAULT_PATIENTS_FILTERS.microbiologyMatch,
  };
}
const DEFAULT_PATIENTS_SORTING: TableSorting = {
  field: 'RISK_SCORE' as string,
  direction: 'desc',
};
const DEFAULT_PATIENTS_PAGING: TablePaging = {};
/** The operator and the value input of a biochemistry row share this width; a value is a short number. */
const BIOCHEMISTRY_VALUE_WIDTH = 128;
/**
 * A biochemistry row must not wrap on a normal screen: lab method (362) + operator + the value field, whose text
 * filter wrapper keeps the standard 176px around the narrower input, the 8px gaps between them, the 2px gap and the
 * 18px remove cross after the value, and the 8px padding on both sides of the box. The filter panel keeps this much
 * for its inputs before the saved-filters column starts to give way.
 */
const BIOCHEMISTRY_ROW_WIDTH = 362 + 8 + BIOCHEMISTRY_VALUE_WIDTH + 8 + 176 + 2 + 18 + 2 * 8;
/**
 * The value field of a biochemistry row is a 128px input inside the standard 176px text-filter wrapper, so its remove
 * cross ends up this far from the input. The microbiology rows have no such wrapper and add the gap themselves, so
 * that the cross sits at the same distance from the last field in both boxes.
 */
const CONDITION_ROW_REMOVE_GAP = 176 - BIOCHEMISTRY_VALUE_WIDTH;

/**
 * The occurrence selects (medication, biochemistry values) are as wide as the date filters next to them (176px, see
 * FilterNodeTypeDateRange). A select without an explicit width keeps the 362px of a full-width filter.
 */
const MATCH_FILTER_WIDTH = 176;

export const HospitalizedPatientsLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'hospitalizationList';
  const storageIdPrefix = '/hospitalizedPatients';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      patientsOpen: query.patientsOpen ?? DEFAULT_PATIENTS_OPEN,
      patientsTab: query.patientsTab ?? DEFAULT_PATIENTS_TAB,
      patients: withOccurrenceDefaults(query?.patients ?? DEFAULT_PATIENTS_FILTERS),
      patientsSorting: query?.patientsSorting ?? DEFAULT_PATIENTS_SORTING,
      patientsPaging: query?.patientsPaging ?? DEFAULT_PATIENTS_PAGING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({patients: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HospitalizedPatientsLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
type RowType = MerieSchema.components['schemas']['EvaluatedEncounterDto'];

function getColumns(formatters: Formatters, defaultSort: TableSorting) {
  if (ENABLE_PH_REC) {
    return getNewColumns(formatters, defaultSort);
  }
  return getOldColumns(formatters, defaultSort);
}
// NOTE: old columns for next release
function getOldColumns(formatters: Formatters, defaultSort: TableSorting): Column<RowType>[] {
  const {t, tEnum, formatDateShort} = formatters;
  const columns: Column<RowType>[] = [
    {
      field: 'patientFullName',
      title: t('patient.list.grid.fullName'),
      tooltip: t('patient.list.grid.fullName.tooltip'),
      render: (data) => (
        <HospitalizationsLinkFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.fullName} data={data} versionSuffix='/v2' />
      ),
      width: '165px',
    },
    {
      field: 'patient.identificationNumber',
      title: t('patient.list.grid.identificationNumber'),
      tooltip: t('patient.list.grid.identificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.identificationNumber} />,
      sorting: false,
    },
    {
      field: 'encounterOrganization',
      title: t('patient.list.grid.admissionDepartment'),
      tooltip: t('patient.list.grid.admissionDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.encounterOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'ENCOUNTER_STARTED_DATE',
      title: t('patient.list.grid.admissionDate'),
      tooltip: t('patient.list.grid.admissionDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.encounterStartedAt} />,
      customSort: () => 0,
    },
    {
      field: 'lastOrganization',
      title: t('patient.list.grid.currentDepartment'),
      tooltip: t('patient.list.grid.currentDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.lastOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'LAST_ORGANIZATION_ADMISSION_DATE',
      title: t('patient.list.grid.transferDate'),
      tooltip: t('patient.list.grid.transferDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.lastOrganizationAdmissionDate} />,
      customSort: () => 0,
    },
    {
      field: 'RISK_SCORE',
      title: t('patient.list.grid.riskScore'),
      tooltip: t('patient.list.grid.riskScore.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.riskScoreValue} />,
    },
    {
      field: 'riskFactorsCount',
      title: t('patient.list.grid.riskFactors'),
      tooltip: t('patient.list.grid.riskFactors.tooltip'),
      render: (data) => <RiskFactorsFormatter value={data.activeRiskFactorsCount ?? data.riskFactorsCount} data={data} />, // NOTE: BE version agnostic
    },
    {
      field: 'RISK_SEVERITY',
      title: t('patient.list.grid.riskSeverity'),
      tooltip: t('patient.list.grid.riskSeverity.tooltip'),
      render: (data) => (
        <TranslateEnumFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.riskSeverity}
          colDef={{cellRendererParams: {enumName: 'RiskSeverityEnum'}}}
        />
      ),
    },
    {
      field: 'EVALUATION_STATE_MODIFIED_DATE',
      title: t('patient.list.grid.checkState'),
      tooltip: t('patient.list.grid.checkState.tooltip'),
      render: (data) => {
        const checkState = data.evaluationState ? tEnum('EvaluationStateEnum', data.evaluationState) : t('global.unknown');
        const checkDate = data.evaluationStateModifiedOn ? formatDateShort(data.evaluationStateModifiedOn) : undefined;
        return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={[checkDate, checkState].filter((v) => v).join(' - ')} />;
      },
      customSort: () => 0,
    },
    /* {
      field: 'lastMedicationsCount',
      title: t('patient.list.grid.medications'),
      tooltip: t('patient.list.grid.medications.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.lastMedicationsCount}
        />
      ),
    }, */
  ];
  return columns.map((v) => ({
    ...v,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}
// NOTE: new columns for demo
function getNewColumns(formatters: Formatters, defaultSort: TableSorting): Column<RowType>[] {
  const {t, tEnum, formatDateShort} = formatters;
  const columns: Column<RowType>[] = [
    {
      field: 'patientFullName',
      title: t('patient.list.grid.fullName'),
      tooltip: t('patient.list.grid.fullName.tooltip'),
      render: (data) => {
        const texts = [data.patient?.fullName ?? ''];
        const label = texts.join(', ');

        const accTooltip = [] as React.ReactNode[];
        accTooltip.push(<span key='label'>{label}</span>);
        // NOTE: tooltip has `maxWidth: 300`
        if (data.comment) {
          accTooltip.push(<hr key='divider' className='tooltip-divider' />);
          accTooltip.push(
            <div key='comment' data-ellipsis='3'>
              {data.comment}
            </div>
          );
        }
        const tooltip = <>{accTooltip}</>;

        return <HospitalizationsLinkFormatter {...AG_GRID_COMPATIBILITY} data={data} value={label} tooltip={tooltip} versionSuffix='/v2' />;
      },
      width: '165px',
    },
    {
      field: 'patient.identificationNumber',
      title: t('patient.list.grid.identificationNumber'),
      tooltip: t('patient.list.grid.identificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.identificationNumber} />,
      sorting: false,
    },
    {
      field: 'encounterOrganization',
      title: t('patient.list.grid.admissionDepartment'),
      tooltip: t('patient.list.grid.admissionDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.encounterOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'ENCOUNTER_STARTED_DATE',
      title: t('patient.list.grid.admissionDate'),
      tooltip: t('patient.list.grid.admissionDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.encounterStartedAt} />,
      customSort: () => 0,
    },
    {
      field: 'lastOrganization',
      title: t('patient.list.grid.currentDepartment'),
      tooltip: t('patient.list.grid.currentDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.lastOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'LAST_ORGANIZATION_ADMISSION_DATE',
      title: t('patient.list.grid.transferDate'),
      tooltip: t('patient.list.grid.transferDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.lastOrganizationAdmissionDate} />,
      customSort: () => 0,
    },
    {
      field: 'RISK_SCORE',
      title: t('patient.list.grid.riskScore'),
      tooltip: t('patient.list.grid.riskScore.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.riskScoreValue} />,
    },
    {
      field: 'riskFactorsCount',
      title: t('patient.list.grid.riskFactors'),
      tooltip: t('patient.list.grid.riskFactors.tooltip'),
      render: (data) => <RiskFactorsFormatter value={data.activeRiskFactorsCount ?? data.riskFactorsCount} data={data} />, // NOTE: BE version agnostic
    },
    {
      field: 'pharmaRecommendationRiskiness',
      title: t('patient.list.grid.pharmaRecommendationRiskiness'),
      tooltip: t('patient.list.grid.pharmaRecommendationRiskiness.tooltip'),
      render: (data) => {
        const {pharmaRecommendationRiskiness} = data;
        if (pharmaRecommendationRiskiness == null) return '-';
        return (
          <ChipsFormatter
            value={[pharmaRecommendationRiskiness]}
            getInfo={(riskiness) => ({
              label: tEnum('Riskiness', riskiness),
              background: RISKINESS_COLORS[riskiness]?.background,
            })}
            tooltip={tEnum('Riskiness', pharmaRecommendationRiskiness)}
          />
        );
      },
      customSort: () => 0,
    },
    {
      field: 'PHARMA_RECOMMENDATION_DATE',
      title: t('patient.list.grid.pharmaRecommendationDate'),
      tooltip: t('patient.list.grid.pharmaRecommendationDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.pharmaRecommendationDate} />,
    },
    {
      field: 'PHARMA_RECOMMENDATION_FOLLOW_UP_DATE',
      title: t('patient.list.grid.pharmaRecommendationFollowUpDate'),
      tooltip: t('patient.list.grid.pharmaRecommendationFollowUpDate.tooltip'),
      render: (data) => {
        const followUpDate = data.pharmaRecommendationFollowUpDate;
        if (followUpDate == null) return '-';

        const text = formatDateShort(followUpDate);
        const {iconName, tooltip} = getFollowUpDateIconTooltip(followUpDate, formatters);

        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={
              <div style={{display: 'inline-flex', alignItems: 'center', wordBreak: 'break-word'}}>
                {iconName && (
                  <DlMuiDynamicIcon
                    iconName={iconName}
                    useFixedViewBox='0 0 14 16'
                    noButtonStyles
                    style={{padding: 0, marginRight: 4}}
                    svgIconStyle={{fontSize: 18}}
                  />
                )}
                <span>{text}</span>
              </div>
            }
            tooltipOverride={tooltip ?? text}
          />
        );
      },
    },
    /* {
      field: 'lastMedicationsCount',
      title: t('patient.list.grid.medications'),
      tooltip: t('patient.list.grid.medications.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.lastMedicationsCount}
        />
      ),
    }, */
  ];
  return columns.map((v) => ({
    ...v,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}
export function normalizeHospitalizedPatientsFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  // The occurrence defaults are part of the comparison too, so a saved filter from before the keys existed still
  // matches the panel that shows it.
  const copy: Record<string, any> = withOccurrenceDefaults(structuredClone(filter ?? {}) as Partial<PatientsFilters>);
  if (copy.encounterFloatingShortcutName) {
    delete (copy as any).encounterStartedFrom;
    delete (copy as any).encounterStartedTo;
  }
  if (copy.lastEncounterSubFloatingShortcutName) {
    delete (copy as any).lastEncounterSubStartedFrom;
    delete (copy as any).lastEncounterSubStartedTo;
  }
  if (copy.pharmaRecommendationFloatingShortcutName) {
    delete (copy as any).pharmaRecommendationDateFrom;
    delete (copy as any).pharmaRecommendationDateTo;
  }
  if (copy.pharmaRecommendationFollowUpFloatingShortcutName) {
    delete (copy as any).pharmaRecommendationFollowUpDateFrom;
    delete (copy as any).pharmaRecommendationFollowUpDateTo;
  }
  if (copy.atcHierarchyFloatingShortcutName) {
    delete (copy as any).atcHierarchyFrom;
    delete (copy as any).atcHierarchyTo;
  }
  if (copy.biochemistryFloatingShortcutName) {
    delete (copy as any).biochemistryFrom;
    delete (copy as any).biochemistryTo;
  }
  if (copy.microbiologyFloatingShortcutName) {
    delete (copy as any).microbiologyFrom;
    delete (copy as any).microbiologyTo;
  }
  return copy;
}
const HospitalizedPatientsLayoutInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  // TODO: PDM-1342 fix number range inputs??
  const intl = useIntl();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const {isLoading: permissionsLoading, userRoles} = usePermissions(config);

  const numberRangesUrl = config.service.url.numberRange;
  const {data: numberRangesData} = useApiQuery([numberRangesUrl], null, {
    axiosConfig: {
      url: numberRangesUrl,
    },
  });
  const {riskScoreRange, riskFactorsRange, ageRange, weightRange, bmiRange} = useMemo(() => {
    return {
      riskScoreRange: numberRangesData?.['EvaluatedEncounterDto.riskScoreValue'] ?? {},
      riskFactorsRange: numberRangesData?.['EvaluatedEncounterDto.riskFactorsCount'] ?? {},
      lastMedicationsRange: numberRangesData?.['EvaluatedEncounterDto.lastMedicationsCount'] ?? {},
      // Other tab (PDM-2749): maxima of the precomputed patient attributes, so the sliders end where the data does.
      ageRange: numberRangesData?.['EvaluatedEncounterDto.ageAtEnd'] ?? {},
      weightRange: numberRangesData?.['EvaluatedEncounterDto.weight'] ?? {},
      bmiRange: numberRangesData?.['EvaluatedEncounterDto.bmi'] ?? {},
    };
  }, [numberRangesData]);

  const hospitalizationsUrl = config.service.url.hospitalizations;
  const hospitalizationParams = useTableParams(
    {
      patientFullName: filters.patients.patientFullName,
      patientIdentificationNumber: filters.patients.patientIdentificationNumber,
      patientSrcId: filters.patients.patientSrcId,
      lastEncounterSubOrganizationIds:
        filters.patients.lastEncounterSubOrganizationIds && filters.patients.lastEncounterSubOrganizationIds.join(','),
      encounterOrganizationIds: filters.patients.encounterOrganizationIds && filters.patients.encounterOrganizationIds.join(','),
      lastEncounterSubStartedFrom: filters.patients.lastEncounterSubStartedFrom,
      lastEncounterSubStartedTo: filters.patients.lastEncounterSubStartedTo,
      lastEncounterSubFloatingShortcutName: filters.patients.lastEncounterSubFloatingShortcutName,
      encounterStartedFrom: filters.patients.encounterStartedFrom,
      encounterStartedTo: filters.patients.encounterStartedTo,
      encounterFloatingShortcutName: filters.patients.encounterFloatingShortcutName,
      hospitalizationStates: filters.patients.hospitalizationStates && filters.patients.hospitalizationStates.join(','),
      riskScoreFrom: filters.patients.riskScoreFrom,
      riskScoreTo: filters.patients.riskScoreTo,
      riskFactorTypes: filters.patients.riskFactorTypes && filters.patients.riskFactorTypes.join(','),
      riskFactorsCountFrom: filters.patients.riskFactorsCountFrom,
      riskFactorsCountTo: filters.patients.riskFactorsCountTo,
      commentSearchString: filters.patients.commentSearchString,
      // lastMedicationsCountFrom: filters.patients.lastMedicationsCount?.start,
      // lastMedicationsCountTo: filters.patients.lastMedicationsCount?.end,
      atcHierarchyIds1: filters.patients.atcHierarchyIds1 && filters.patients.atcHierarchyIds1.join(','),
      atcHierarchyIds2: filters.patients.atcHierarchyIds2 && filters.patients.atcHierarchyIds2.join(','),
      atcHierarchyIds3: filters.patients.atcHierarchyIds3 && filters.patients.atcHierarchyIds3.join(','),
      activeIngredientIds1: filters.patients.activeIngredientIds1 && filters.patients.activeIngredientIds1.join(','),
      activeIngredientIds2: filters.patients.activeIngredientIds2 && filters.patients.activeIngredientIds2.join(','),
      activeIngredientIds3: filters.patients.activeIngredientIds3 && filters.patients.activeIngredientIds3.join(','),
      atcHierarchyFrom: filters.patients.atcHierarchyFrom,
      atcHierarchyTo: filters.patients.atcHierarchyTo,
      atcHierarchyFloatingShortcutName: filters.patients.atcHierarchyFloatingShortcutName,
      atcHierarchyMatch: filters.patients.atcHierarchyMatch,
      ...biochemistryParams(filters.patients),
      biochemistryFrom: filters.patients.biochemistryFrom,
      biochemistryTo: filters.patients.biochemistryTo,
      biochemistryFloatingShortcutName: filters.patients.biochemistryFloatingShortcutName,
      biochemistryMatch: filters.patients.biochemistryMatch,
      ...microbiologyParams(filters.patients),
      microbiologyFrom: filters.patients.microbiologyFrom,
      microbiologyTo: filters.patients.microbiologyTo,
      microbiologyFloatingShortcutName: filters.patients.microbiologyFloatingShortcutName,
      microbiologyMatch: filters.patients.microbiologyMatch,
      ...otherParams(filters.patients),
      ...(ENABLE_PH_REC
        ? {
            pharmaRecommendationRiskinesses:
              filters.patients.pharmaRecommendationRiskinesses && filters.patients.pharmaRecommendationRiskinesses.join(','),
            pharmaRecommendationDateFrom: filters.patients.pharmaRecommendationDateFrom,
            pharmaRecommendationDateTo: filters.patients.pharmaRecommendationDateTo,
            pharmaRecommendationFloatingShortcutName: filters.patients.pharmaRecommendationFloatingShortcutName,
            pharmaRecommendationFollowUpDateFrom: filters.patients.pharmaRecommendationFollowUpDateFrom,
            pharmaRecommendationFollowUpDateTo: filters.patients.pharmaRecommendationFollowUpDateTo,
            pharmaRecommendationFollowUpFloatingShortcutName: filters.patients.pharmaRecommendationFollowUpFloatingShortcutName,
          }
        : {
            riskSeverities: filters.patients.riskSeverities && filters.patients.riskSeverities.join(','),
            evaluationStates: filters.patients.evaluationStates && filters.patients.evaluationStates.join(','),
            evaluationStateModifiedFrom: filters.patients.evaluationStateModifiedFrom,
            evaluationStateModifiedTo: filters.patients.evaluationStateModifiedTo,
          }),

      page: filters.patientsPaging.page ?? 0,
      size: filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    },
    filters.patientsSorting,
    {
      patientFullName: ['PATIENT_LAST_NAME', 'PATIENT_FIRST_NAME'],
      pharmaRecommendationRiskiness: 'PHARMA_RECOMMENDATION_RISKINESS',
      riskFactorsCount: 'RISK_FACTORS_COUNT',
    }
  );
  // A biochemistry row with a non-numeric value cannot be sent and must not be silently dropped either, so the
  // list is shown empty without asking the backend and the export is blocked until the value is corrected.
  const listBlocked = hasBlockingBiochemistryRow(filters.patients);
  const tableData = useTableData<any, RowType[]>(hospitalizationsUrl, hospitalizationParams, 'content', !listBlocked);
  const data = listBlocked ? [] : tableData.data;
  const isLoading = listBlocked ? false : tableData.isLoading;
  const page = listBlocked ? 0 : tableData.page;
  const countOnPage = listBlocked ? 0 : tableData.countOnPage;
  const totalCount = listBlocked ? 0 : tableData.totalCount;
  const totalPages = listBlocked ? 0 : tableData.totalPages;
  const {handleXlsxExport, warningDialog} = useHospitalizedPatientsExport(filters.patients, config, totalCount);
  const formatters = useFormatters();
  const {t, formatDate, tEnum} = formatters;
  const isInitialLoad = useRef(true);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  // Last line of the ATC / active-ingredient slots (the ingredients); a newly added slot never lands below it.
  const medicationSlotsEndRef = useRef<HTMLDivElement | null>(null);
  // Fullscreen results table, the same way the report tables do it.
  const {
    maximized: tableMaximized,
    toggleMaximized: tableToggleMaximized,
    MaximizeWrapper: TableMaximizeWrapper,
  } = useMaximizeWrapper('patient-list-wrapper', {innerStyle: {rowGap: 16}});
  // Fullscreen filter panel (PDM-2842): this wrapper IS the panel's card (16px padding, no bottom padding so the
  // content scrolls to the card edge); the panel itself owns the button and keeps the flag consistent with "open".
  const {
    maximized: filterPanelMaximized,
    setMaximized: setFilterPanelMaximized,
    MaximizeWrapper: FilterPanelMaximizeWrapper,
  } = useMaximizeWrapper('filter-panel-wrapper', {innerStyle: {rowGap: 16, paddingBottom: 0}});
  React.useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    } else if (filtersRef.current && medicationSlotsEndRef.current?.offsetParent) {
      // Only while the medication tab is shown - the panel stays mounted (hidden) on the other tabs, where a chip
      // removed from the chips row would otherwise scroll the filters to a zero-height box.
      // Reveal a newly added slot: scroll just enough to bring the slot lines into view. Not the whole tab panel -
      // the date row below them is usually left just under the fold by the divider and must not pull the view down.
      const scrollBox = filtersRef.current;
      const overflow = getScrollOffsetBottom(scrollBox, medicationSlotsEndRef.current) - scrollBox.clientHeight;
      if (overflow > 0) scrollBox.scrollTo(0, scrollBox.scrollTop + overflow);
    }
  }, [
    filtersRef,
    medicationSlotsEndRef,
    // eslint-disable-next-line
    JSON.stringify([...ATC_SLOT_NAMES, ...ACTIVE_INGREDIENT_SLOT_NAMES].map((name) => filters.patients[name])),
  ]);
  const {renderOption: atcRenderOption, renderTag: atcRenderTag, getNodeIcon: atcGetNodeIcon} = useInactiveAtcRenderProps(t);
  const {data: organizations} = useOrganizations(config);
  const {data: medicationAtcs} = useMedicationAtcs<{id?: number | string; label?: string}>(config);
  const {data: activeIngredients} = useMedications(config, 'ACTIVE_INGREDIENT');
  const currentLocale = useCurrentLocale();
  // The endpoint returns active ingredients unsorted; sort them by name the same way the ATC filter does.
  const activeIngredientOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort', numeric: false});
    return activeIngredients.map((v) => ({id: String(v.id), label: v.name})).sort((a, b) => collator.compare(a.label, b.label));
  }, [activeIngredients, currentLocale]);
  // Other tab (PDM-2749): the ICD codes core has seen on structured diagnoses of hospitalizations, code first so
  // that typing a code finds it; the hospital's description follows when there is one.
  const icdListUrl = config.service.url.icdList;
  const {data: icdCodes} = useApiQuery<{code: string; label?: string | null}[]>([icdListUrl], null, {
    axiosConfig: {url: icdListUrl},
  });
  const icdOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort', numeric: true});
    return (icdCodes ?? [])
      .map((v) => ({id: v.code, label: formatIcdOptionLabel(v.code, v.label)}))
      .sort((a, b) => collator.compare(a.id, b.id));
  }, [icdCodes, currentLocale]);
  const getIcdLabel = (code: string) => icdOptions.find((option) => option.id === code)?.label ?? code;
  const labMethodTypesUrl = `${config.service.url.chartRoot}/supported-event-types-v3`;
  const {data: labMethodTypes} = useApiQuery<WallabyCoreSchema.components['schemas']['LabResultEventTypeV3Dto'][]>(
    [labMethodTypesUrl],
    null,
    {axiosConfig: {url: labMethodTypesUrl}}
  );
  // Same ordering as the lab detail screen: hospital-named methods first, our technical [CODE] names after,
  // each subgroup A-Z. A method with no label falls back to its code, which is what formatLabMethodName does.
  const labMethodOptions = useMemo(() => {
    return (labMethodTypes ?? [])
      .map((type) => ({
        id: type.eventType,
        label: formatLabMethodName({eventType: type.eventType, label: type.label, unit: type.unit}),
      }))
      .sort((a, b) => {
        const technicalA = a.label.startsWith('[') ? 1 : 0;
        const technicalB = b.label.startsWith('[') ? 1 : 0;
        return technicalA - technicalB || a.label.localeCompare(b.label, currentLocale);
      });
  }, [labMethodTypes, currentLocale]);
  // Microbiology box (PDM-2748). Pathogens come from core's finding-code catalogue, narrowed here to the categories
  // lab_result_list can contain - BODY_CELLS findings are dropped there, so offering them would guarantee an empty
  // list. The catalogue label is whatever the NLP stored on first import - English, Czech from the seed list, or for
  // Datlowe's own concepts (999xx) just the code - so both lists are labelled through the SNOMED resource like the
  // Labs and Antibiogram screens, catalogue label last.
  const pathogenCodesUrl = config.service.url.labResultFindingCodes;
  const {data: pathogenCodes} = useApiQuery<{code: number; category: string; label: string}[]>([pathogenCodesUrl], null, {
    axiosConfig: {url: pathogenCodesUrl},
  });
  const {snomedLabels} = useSnomedLabels(config);
  const pathogenOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort'});
    return (pathogenCodes ?? [])
      .filter((v) => v.category === 'HAI_PATHOGEN' || v.category === 'OTHER_PATHOGEN')
      .map((v) => ({id: String(v.code), label: snomedLabels[v.code] || v.label}))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [pathogenCodes, snomedLabels, currentLocale]);
  const atbList = useAtbList(config);
  const resistanceOptions = useMemo(() => {
    const collator = new Intl.Collator(currentLocale, {sensitivity: 'base', usage: 'sort'});
    return atbList.map((v) => ({id: String(v.code), label: v.label})).sort((a, b) => collator.compare(a.label, b.label));
  }, [atbList, currentLocale]);
  // Chips pane input: both occurrence selectors only matter once their box has two conditions to compare
  // (the inputs themselves are conditional the same way), so hide their chips otherwise.
  const tableFiltersForSelectedPane = useMemo(() => {
    const result: Record<string, any> = {...filters.patients};
    if (countMedicationConditions(filters.patients) < 2) {
      delete result.atcHierarchyMatch;
    }
    if (countBiochemistryItems(filters.patients) < 2) {
      delete result.biochemistryMatch;
    }
    if (countMicrobiologyConditions(filters.patients) < 2) {
      delete result.microbiologyMatch;
    }
    // An unfinished row must not show up as a chip, so its parts are hidden from the pane as well.
    BIOCHEMISTRY_ROWS.filter((row) => !isBiochemistryRowComplete(filters.patients, row)).forEach((row) => {
      delete result[row.method];
      delete result[row.operator];
      delete result[row.value];
    });
    return result;
  }, [filters.patients]);

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }
  if (!userRoles.HOSPITALIZATIONS_VIEW_ROLE) {
    // TODO: move this out to AppLayoutContent?
    return <Typography variant='h1'>{utils.TranslateUtils.translate('permissions.insuficientrights', intl)}</Typography>;
  }
  const sortByMessageKey = (options) => sortBy(options, [(v) => t(v.messageKey)]);
  const getOrganizationLabel = (id: unknown) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id);
  const getAtcLabel = (id: unknown) => medicationAtcs?.find((v) => String(v.id) === String(id))?.label ?? String(id);
  const getActiveIngredientLabel = (id: unknown) => activeIngredientOptions.find((v) => String(v.id) === String(id))?.label ?? String(id);
  const getPathogenLabel = (id: unknown) => pathogenOptions.find((v) => v.id === String(id))?.label ?? String(id);
  const getResistanceLabel = (id: unknown) => resistanceOptions.find((v) => v.id === String(id))?.label ?? String(id);
  const hasFilters = !isEqual(
    normalizeHospitalizedPatientsFiltersForCompare(omitNullsy(filters.patients) as any),
    normalizeHospitalizedPatientsFiltersForCompare(omitNullsy(DEFAULT_PATIENTS_FILTERS) as any)
  );
  const columns = getColumns(formatters, filters.patientsSorting);
  const atcSlots = ATC_SLOT_NAMES.map((name) => filters.patients[name]);
  const activeIngredientSlots = ACTIVE_INGREDIENT_SLOT_NAMES.map((name) => filters.patients[name]);
  const showAtcSlot = getMultiFilter(atcSlots);
  const showActiveIngredientSlot = getMultiFilter(activeIngredientSlots);
  const showMedicationMatch = countMedicationConditions(filters.patients) >= 2;
  const showBiochemistryRow = showBiochemistryRows(filters.patients);
  // The occurrence selector appears once two distinct methods are set.
  const showBiochemistryMatch = countBiochemistryItems(filters.patients) >= 2;
  const showMicrobiologyRow = showMicrobiologyRows(filters.patients);
  const showMicrobiologyMatch = countMicrobiologyConditions(filters.patients) >= 2;
  const operatorOptions = enumMap.LabValueOperatorEnum ?? [];
  const getBiochemistryConditionLabel = (patients: PatientsFilters, row: BiochemistryRow) => {
    if (!isBiochemistryRowComplete(patients, row)) return '';
    const method = patients[row.method] ?? '';
    const methodLabel = labMethodOptions.find((option) => option.id === method)?.label ?? method;
    const operator = tEnum('LabValueOperatorEnum', patients[row.operator]);
    // No unit appended - formatLabMethodName already carries it inside the method name.
    return [methodLabel, operator, patients[row.value]].filter(Boolean).join(' ');
  };
  /** Rows close ranks once one is emptied, the same way the medication slots above do. */
  const changeBiochemistryRow = (row: BiochemistryRow, part: 'method' | 'operator' | 'value', newValue: string | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...applyBiochemistryRowChange(filters.patients, row, part, newValue),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  const removeBiochemistryRowFilter = (row: BiochemistryRow) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...removeBiochemistryRow(filters.patients, row),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  /** A microbiology row packs the same way; the two multiselects of a row move together. */
  const changeMicrobiologyRow = (row: MicrobiologyRow, part: MicrobiologyPart, newValue: string[] | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...applyMicrobiologyRowChange(filters.patients, row, part, newValue),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  const removeMicrobiologyRowFilter = (row: MicrobiologyRow) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...removeMicrobiologyRow(filters.patients, row),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  /** Clearing a slot moves the following ones of its line up, so the box never shows an empty slot above a filled one. */
  const changeMedicationSlot = (name: MedicationSlotName, newValue: string[] | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...packMedicationSlots({...filters.patients, [name]: newValue ?? undefined}),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  // Other tab (PDM-2749): plain independent fields, so one setter with the paging reset serves them all.
  const changeOther = (patch: Partial<PatientsFilters>) =>
    changeFilters({
      patients: {...filters.patients, ...patch},
      patientsPaging: {...filters.patientsPaging, page: 0},
    });
  const numberRange = (from: number | undefined, to: number | undefined) => (from != null && to != null ? {start: from, end: to} : null);
  // Every chip belongs to one filter-panel tab: the tab badges count the chips of their tab, the "Active filters"
  // heading counts them all. The lists mirror the field order of the tab panels below.
  const withTab = (tab: HospitalizationFilterTab, items: SelectedFilter[]): SelectedFilter[] => items.map((v) => ({...v, tab}));
  const selectedFilters: SelectedFilter[] = [
    ...withTab('general', [
      {
        name: 'patientFullName',
        label: t('filter.patient.fullName'),
        value: filters.patients.patientFullName,
      },
      {
        name: 'patientIdentificationNumber',
        label: t('filter.patient.identificationNumber'),
        value: filters.patients.patientIdentificationNumber,
      },
      {
        name: 'patientSrcId',
        label: t('filter.patient.sourceId'),
        value: filters.patients.patientSrcId,
      },
      {
        name: 'encounterOrganizationIds',
        label: t('filter.patient.admissionDepartment'),
        value: (filters.patients.encounterOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['encounterStartedFrom', 'encounterStartedTo', 'encounterFloatingShortcutName'],
        label: t('filter.patient.admissionDate'),
        value: `${formatDate(filters.patients.encounterStartedFrom ?? '')} - ${formatDate(filters.patients.encounterStartedTo ?? '')}`,
      },
      {
        name: 'lastEncounterSubOrganizationIds',
        label: t('filter.patient.currentDepartment'),
        value: (filters.patients.lastEncounterSubOrganizationIds ?? []).map(getOrganizationLabel).join(', '),
      },
      {
        name: ['lastEncounterSubStartedFrom', 'lastEncounterSubStartedTo', 'lastEncounterSubFloatingShortcutName'],
        label: t('filter.patient.transferDate'),
        value: `${formatDate(filters.patients.lastEncounterSubStartedFrom ?? '')} - ${formatDate(
          filters.patients.lastEncounterSubStartedTo ?? ''
        )}`,
      },
      {
        name: ['riskScoreFrom', 'riskScoreTo'],
        label: t('filter.patient.riskScore'),
        value: `${filters.patients.riskScoreFrom ?? ''} - ${filters.patients.riskScoreTo ?? ''}`,
      },
      {
        name: 'riskFactorTypes',
        label: t('filter.patient.riskFactorTypes'),
        value: (filters.patients.riskFactorTypes ?? []).map((v) => tEnum('RiskFactorTypeEnum', v)).join(', '),
      },
      {
        name: ['riskFactorsCountFrom', 'riskFactorsCountTo'],
        label: t('filter.patient.riskFactors'),
        value: `${filters.patients.riskFactorsCountFrom ?? ''} - ${filters.patients.riskFactorsCountTo ?? ''}`,
      },
      ...(ENABLE_PH_REC
        ? [
            {
              name: 'pharmaRecommendationRiskinesses',
              label: t('filter.patient.pharmaRecommendationRiskinesses'),
              value: (filters.patients.pharmaRecommendationRiskinesses ?? [])
                .map((v) => tEnum('RiskinessHospitalizationFilter', v))
                .join(', '),
            },
            {
              name: ['pharmaRecommendationDateFrom', 'pharmaRecommendationDateTo', 'pharmaRecommendationFloatingShortcutName'],
              label: t('filter.patient.pharmaRecommendationDate'),
              value: `${formatDate(filters.patients.pharmaRecommendationDateFrom ?? '')} - ${formatDate(
                filters.patients.pharmaRecommendationDateTo ?? ''
              )}`,
            },
            {
              name: [
                'pharmaRecommendationFollowUpDateFrom',
                'pharmaRecommendationFollowUpDateTo',
                'pharmaRecommendationFollowUpFloatingShortcutName',
              ],
              label: t('filter.patient.pharmaRecommendationFollowUpDate'),
              value: `${formatDate(filters.patients.pharmaRecommendationFollowUpDateFrom ?? '')} - ${formatDate(
                filters.patients.pharmaRecommendationFollowUpDateTo ?? ''
              )}`,
            },
          ]
        : [
            {
              name: 'riskSeverities',
              label: t('filter.patient.riskSeverity'),
              value: (filters.patients.riskSeverities ?? []).map((v) => tEnum('RiskSeverityEnum', v)).join(', '),
            },
            {
              name: 'evaluationStates',
              label: t('filter.patient.checkState'),
              value: (filters.patients.evaluationStates ?? []).map((v) => tEnum('EvaluationStateEnum', v)).join(', '),
            },
            {
              name: ['evaluationStateModifiedFrom', 'evaluationStateModifiedTo'],
              label: t('filter.patient.checkDate'),
              value: `${formatDate(filters.patients.evaluationStateModifiedFrom ?? '')} - ${formatDate(
                filters.patients.evaluationStateModifiedTo ?? ''
              )}`,
            },
          ]),
      {
        name: 'hospitalizationStates',
        label: t('filter.patient.hospitalizations'),
        value: (filters.patients.hospitalizationStates ?? []).map((v) => tEnum('HospitalizationStateEnum', v)).join(', '),
      },
      {
        name: 'commentSearchString',
        label: t('filter.patient.commentSearchString'),
        value: filters.patients.commentSearchString,
      },
    ]),
    ...withTab('medication', [
      ...ATC_SLOT_NAMES.map((name, index) => ({
        name,
        label: t(ATC_SLOT_LABELS[index]),
        value: (filters.patients[name] ?? []).map(getAtcLabel).join(', '),
      })),
      ...ACTIVE_INGREDIENT_SLOT_NAMES.map((name, index) => ({
        name,
        label: t(ACTIVE_INGREDIENT_SLOT_LABELS[index]),
        value: (filters.patients[name] ?? []).map(getActiveIngredientLabel).join(', '),
      })),
      {
        name: ['atcHierarchyFrom', 'atcHierarchyTo', 'atcHierarchyFloatingShortcutName'],
        label: t('filter.patient.atcHierarchyDate'),
        value: `${formatDate(filters.patients.atcHierarchyFrom ?? '')} - ${formatDate(filters.patients.atcHierarchyTo ?? '')}`,
      },
      {
        name: 'atcHierarchyMatch',
        label: t('filter.patient.atcHierarchyMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', filters.patients.atcHierarchyMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.atcHierarchyMatch,
      },
    ]),
    ...withTab('laboratory', [
      ...BIOCHEMISTRY_ROWS.map((row) => ({
        // Removing the chip has to clear the whole row, not just one of its three parts.
        name: [row.method, row.operator, row.value],
        label: t(row.label),
        value: getBiochemistryConditionLabel(filters.patients, row),
      })),
      {
        name: ['biochemistryFrom', 'biochemistryTo', 'biochemistryFloatingShortcutName'],
        label: t('filter.patient.biochemistryDate'),
        value: `${formatDate(filters.patients.biochemistryFrom ?? '')} - ${formatDate(filters.patients.biochemistryTo ?? '')}`,
      },
      {
        name: 'biochemistryMatch',
        label: t('filter.patient.biochemistryMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', filters.patients.biochemistryMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.biochemistryMatch,
      },
    ]),
    ...withTab('microbiology', [
      // A row is two chips, the way the design draws it: the pathogens and the resistances can be cleared separately.
      ...MICROBIOLOGY_ROWS.flatMap((row) => [
        {
          name: row.pathogens,
          label: t(row.pathogensLabel),
          value: (filters.patients[row.pathogens] ?? []).map(getPathogenLabel).join(', '),
        },
        {
          name: row.resistances,
          label: t(row.resistancesLabel),
          value: (filters.patients[row.resistances] ?? []).map(getResistanceLabel).join(', '),
        },
      ]),
      {
        name: ['microbiologyFrom', 'microbiologyTo', 'microbiologyFloatingShortcutName'],
        label: t('filter.patient.microbiologyDate'),
        value: `${formatDate(filters.patients.microbiologyFrom ?? '')} - ${formatDate(filters.patients.microbiologyTo ?? '')}`,
      },
      {
        name: 'microbiologyMatch',
        label: t('filter.patient.microbiologyMatch'),
        value: tEnum('AtcHierarchyFilterMatchEnum', filters.patients.microbiologyMatch),
        defaultValue: DEFAULT_PATIENTS_FILTERS.microbiologyMatch,
      },
    ]),
    ...withTab('other', [
      {
        name: 'icdCodes',
        label: t('filter.patient.icdCodes'),
        value: (filters.patients.icdCodes ?? []).map(getIcdLabel).join(', '),
      },
      {
        name: ['ageFrom', 'ageTo'],
        label: t('filter.patient.age'),
        value: `${filters.patients.ageFrom ?? ''} - ${filters.patients.ageTo ?? ''}`,
      },
      {
        name: ['weightFrom', 'weightTo'],
        label: t('filter.patient.weight'),
        value: `${filters.patients.weightFrom ?? ''} - ${filters.patients.weightTo ?? ''}`,
      },
      {
        name: ['bmiFrom', 'bmiTo'],
        label: t('filter.patient.bmi'),
        value: `${filters.patients.bmiFrom ?? ''} - ${filters.patients.bmiTo ?? ''}`,
      },
      {
        name: 'deathDuringHospitalizations',
        label: t('filter.patient.deathDuringHospitalization'),
        value: (filters.patients.deathDuringHospitalizations ?? []).map((v) => tEnum('DeathDuringHospitalizationEnum', v)).join(', '),
      },
    ]),
  ];
  const selectedFiltersCount = countSelectedFilters(selectedFilters, tableFiltersForSelectedPane);
  const filterTabs: LightFilterTabDef[] = [
    {id: 'general', label: t('filter.tabs.general'), iconName: 'hospitalUserIcon', count: selectedFiltersCount.byTab.general},
    {id: 'medication', label: t('filter.tabs.medication'), iconName: 'pillsIcon', count: selectedFiltersCount.byTab.medication},
    {
      id: 'laboratory',
      label: t('patient.detail.healthcare.eventType.laboratory'),
      iconName: 'flaskIcon',
      count: selectedFiltersCount.byTab.laboratory,
    },
    {
      id: 'microbiology',
      label: t('filter.tabs.microbiology'),
      iconName: 'bacteriumIcon',
      count: selectedFiltersCount.byTab.microbiology,
    },
    {id: 'other', label: t('filter.tabs.other'), iconName: 'slidersIcon', count: selectedFiltersCount.byTab.other},
  ];
  const selectedFiltersPane =
    selectedFiltersCount.total > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={selectedFilters}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) => {
          // Removing a biochemistry or medication chip empties its row or slot, so the ones below it move up the
          // same way they do when it is cleared in the form.
          const patients = {...filters.patients, ...newTableFilters};
          changeFilters({
            patients: {
              ...patients,
              ...packBiochemistryRows(patients),
              ...packMedicationSlots(patients),
            },
            patientsPaging: {
              ...filters.patientsPaging,
              page: 0,
            },
          });
        }}
        formatters={formatters}
      />
    ) : null;
  // The inputs of every tab; the panel keeps all of them mounted and shows the active one.
  const tabPanels: Record<HospitalizationFilterTab, React.ReactNode> = {
    general: (
      <>
        <LightTextFilter
          config={{
            name: 'patientFullName',
            ui: {label: 'filter.patient.fullName'},
          }}
          value={filters.patients.patientFullName}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientFullName: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientIdentificationNumber',
            ui: {label: 'filter.patient.identificationNumber'},
          }}
          value={filters.patients.patientIdentificationNumber}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientIdentificationNumber: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientSrcId',
            ui: {label: 'filter.patient.sourceId'},
          }}
          value={filters.patients.patientSrcId}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientSrcId: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightOrganizationFilter2
          appConfig={config}
          config={{
            name: 'encounterOrganizationIds',
            ui: {
              label: 'filter.patient.admissionDepartment',
            },
          }}
          multi
          value={filters.patients.encounterOrganizationIds}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                encounterOrganizationIds: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'admissionDate',
            ui: {label: 'filter.patient.admissionDate'},
          }}
          value={
            filters.patients.encounterStartedFrom && filters.patients.encounterStartedTo
              ? {
                  startDate: filters.patients.encounterStartedFrom,
                  endDate: filters.patients.encounterStartedTo,
                  shortcut: filters.patients.encounterFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                encounterStartedFrom: newValue?.startDate,
                encounterStartedTo: newValue?.endDate,
                encounterFloatingShortcutName: newValue?.shortcut,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightOrganizationFilter2
          appConfig={config}
          config={{
            name: 'lastEncounterSubOrganizationIds',
            ui: {
              label: 'filter.patient.currentDepartment',
            },
          }}
          multi
          value={filters.patients.lastEncounterSubOrganizationIds}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                lastEncounterSubOrganizationIds: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'transferDate',
            ui: {label: 'filter.patient.transferDate'},
          }}
          value={
            filters.patients.lastEncounterSubStartedFrom && filters.patients.lastEncounterSubStartedTo
              ? {
                  startDate: filters.patients.lastEncounterSubStartedFrom,
                  endDate: filters.patients.lastEncounterSubStartedTo,
                  shortcut: filters.patients.lastEncounterSubFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                lastEncounterSubStartedFrom: newValue?.startDate,
                lastEncounterSubStartedTo: newValue?.endDate,
                lastEncounterSubFloatingShortcutName: newValue?.shortcut,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightNumberRangeFilter
          config={{
            name: 'riskScoreValue',
            ui: {
              label: 'filter.patient.riskScore',
              ...riskScoreRange,
            },
          }}
          value={
            filters.patients.riskScoreFrom != null && filters.patients.riskScoreTo != null
              ? {
                  start: filters.patients.riskScoreFrom,
                  end: filters.patients.riskScoreTo,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskScoreFrom: newValue?.start,
                riskScoreTo: newValue?.end,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'riskFactorTypes',
            ui: {label: 'filter.patient.riskFactorTypes'},
          }}
          options={sortByMessageKey(enumMap.RiskFactorTypeEnum ?? [])}
          multi
          value={filters.patients.riskFactorTypes}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskFactorTypes: newValue,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightNumberRangeFilter
          config={{
            name: 'riskFactorsCount',
            ui: {
              label: 'filter.patient.riskFactors',
              ...riskFactorsRange,
            },
          }}
          value={
            filters.patients.riskFactorsCountFrom != null && filters.patients.riskFactorsCountTo != null
              ? {
                  start: filters.patients.riskFactorsCountFrom,
                  end: filters.patients.riskFactorsCountTo,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskFactorsCountFrom: newValue?.start,
                riskFactorsCountTo: newValue?.end,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        {ENABLE_PH_REC ? (
          <>
            <LightSelectFilter
              config={{
                name: 'pharmaRecommendationRiskinesses',
                ui: {label: 'filter.patient.pharmaRecommendationRiskinesses'},
              }}
              options={sortByMessageKey(enumMap.RiskinessHospitalizationFilter ?? [])}
              multi
              value={filters.patients.pharmaRecommendationRiskinesses}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationRiskinesses: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightDateRangeFilter
              config={{
                name: 'pharmaRecommendationDate',
                ui: {label: 'filter.patient.pharmaRecommendationDate'},
              }}
              value={
                filters.patients.pharmaRecommendationDateFrom && filters.patients.pharmaRecommendationDateTo
                  ? {
                      startDate: filters.patients.pharmaRecommendationDateFrom,
                      endDate: filters.patients.pharmaRecommendationDateTo,
                      shortcut: filters.patients.pharmaRecommendationFloatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationDateFrom: newValue?.startDate,
                    pharmaRecommendationDateTo: newValue?.endDate,
                    pharmaRecommendationFloatingShortcutName: newValue?.shortcut,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightDateRangeFilter
              config={{
                name: 'pharmaRecommendationFollowUpDate',
                ui: {label: 'filter.patient.pharmaRecommendationFollowUpDate'},
              }}
              value={
                filters.patients.pharmaRecommendationFollowUpDateFrom && filters.patients.pharmaRecommendationFollowUpDateTo
                  ? {
                      startDate: filters.patients.pharmaRecommendationFollowUpDateFrom,
                      endDate: filters.patients.pharmaRecommendationFollowUpDateTo,
                      shortcut: filters.patients.pharmaRecommendationFollowUpFloatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationFollowUpDateFrom: newValue?.startDate,
                    pharmaRecommendationFollowUpDateTo: newValue?.endDate,
                    pharmaRecommendationFollowUpFloatingShortcutName: newValue?.shortcut,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              enableFuture
              showShortcuts
            />
          </>
        ) : (
          <>
            <LightSelectFilter
              config={{
                name: 'riskSeverities',
                ui: {label: 'filter.patient.riskSeverity'},
              }}
              options={sortByMessageKey(enumMap.RiskSeverityEnum ?? [])}
              multi
              value={filters.patients.riskSeverities}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    riskSeverities: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: 'evaluationStates',
                ui: {label: 'filter.patient.checkState'},
              }}
              options={sortByMessageKey(enumMap.EvaluationStateEnum ?? [])}
              multi
              value={filters.patients.evaluationStates}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    evaluationStates: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightDateRangeFilter
              config={{
                name: 'checkDate',
                ui: {label: 'filter.patient.checkDate'},
              }}
              value={
                filters.patients.evaluationStateModifiedFrom && filters.patients.evaluationStateModifiedTo
                  ? {
                      startDate: filters.patients.evaluationStateModifiedFrom,
                      endDate: filters.patients.evaluationStateModifiedTo,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    evaluationStateModifiedFrom: newValue?.startDate,
                    evaluationStateModifiedTo: newValue?.endDate,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
          </>
        )}
        <LightSelectFilter
          config={{
            name: 'hospitalizationStates',
            ui: {label: 'filter.patient.hospitalizations'},
          }}
          options={sortByMessageKey(enumMap.HospitalizationStateEnum ?? [])}
          multi
          value={filters.patients.hospitalizationStates}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                hospitalizationStates: newValue,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'commentSearchString',
            ui: {label: 'filter.patient.commentSearchString'},
          }}
          value={filters.patients.commentSearchString}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                commentSearchString: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
      </>
    ),
    medication: (
      <div css={muiStyles.tabPanel}>
        <div css={muiStyles.medicationsFilterLine}>
          {ATC_SLOT_NAMES.map(
            (name, index) =>
              showAtcSlot[index] && (
                <React.Fragment key={name}>
                  {index > 0 && <div css={muiStyles.medicationsFilterJoiner}>&</div>}
                  <LightMedicationAtcFilter2
                    css={muiStyles.medicationsFilter}
                    appConfig={config}
                    config={{
                      name,
                      ui: {label: ATC_SLOT_LABELS[index]},
                    }}
                    multi
                    value={atcSlots[index] ?? null}
                    onChange={(newValue) => changeMedicationSlot(name, newValue)}
                    getNodeIcon={(params) => atcGetNodeIcon(params.node)}
                    renderOption={atcRenderOption}
                    renderTag={atcRenderTag}
                  />
                </React.Fragment>
              )
          )}
        </div>
        <div ref={medicationSlotsEndRef} css={muiStyles.medicationsFilterLine}>
          {ACTIVE_INGREDIENT_SLOT_NAMES.map(
            (name, index) =>
              showActiveIngredientSlot[index] && (
                <React.Fragment key={name}>
                  {index > 0 && <div css={muiStyles.medicationsFilterJoiner}>&</div>}
                  <LightSelectFilterVirtualized
                    css={muiStyles.medicationsFilter}
                    config={{
                      name,
                      ui: {label: ACTIVE_INGREDIENT_SLOT_LABELS[index]},
                    }}
                    options={activeIngredientOptions}
                    multi
                    value={activeIngredientSlots[index] ?? null}
                    onChange={(newValue) => changeMedicationSlot(name, newValue)}
                  />
                </React.Fragment>
              )
          )}
        </div>
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'atcHierarchyDate',
              ui: {label: 'filter.patient.atcHierarchyDate'},
            }}
            value={
              filters.patients.atcHierarchyFrom && filters.patients.atcHierarchyTo
                ? {
                    startDate: filters.patients.atcHierarchyFrom,
                    endDate: filters.patients.atcHierarchyTo,
                    shortcut: filters.patients.atcHierarchyFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  atcHierarchyFrom: newValue?.startDate,
                  atcHierarchyTo: newValue?.endDate,
                  atcHierarchyFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
            isMedications
          />
          {showMedicationMatch && (
            <LightSelectFilter
              config={{
                name: 'atcHierarchyMatch',
                ui: {label: 'filter.patient.atcHierarchyMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.atcHierarchyMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    atcHierarchyMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    laboratory: (
      <div css={muiStyles.tabPanel}>
        {BIOCHEMISTRY_ROWS.map(
          (row, index) =>
            showBiochemistryRow[index] && (
              <div css={muiStyles.conditionRowBox} key={row.method}>
                <LightSelectFilterVirtualized
                  css={muiStyles.medicationsFilter}
                  config={{name: row.method, ui: {label: row.label}}}
                  options={labMethodOptions}
                  value={filters.patients[row.method] ?? null}
                  onChange={(newValue) => changeBiochemistryRow(row, 'method', newValue)}
                />
                <LightSelectFilter
                  css={muiStyles.medicationsFilter}
                  // Same width as the value input next to it. The wrapper takes the width from these props, a `css`
                  // override does not reach it. The inline style makes the width definite: with only max-width the
                  // field would still be laid out at 128px, but the box around the row sizes itself to its
                  // content and would count the select's narrower intrinsic width - and wrap the value field.
                  maxWidth={BIOCHEMISTRY_VALUE_WIDTH}
                  style={{width: BIOCHEMISTRY_VALUE_WIDTH}}
                  config={{name: row.operator, ui: {label: row.operatorLabel}}}
                  options={operatorOptions}
                  value={filters.patients[row.operator] ?? null}
                  onChange={(newValue) => changeBiochemistryRow(row, 'operator', newValue)}
                />
                {/* The cross stays glued to the value field, so a wrapped row never drops it on a line of its own. */}
                <span css={muiStyles.biochemistryValueWithRemove}>
                  <LightTextFilter
                    config={{name: row.value, ui: {label: row.valueLabel}}}
                    value={filters.patients[row.value]}
                    onChange={(newValue) => changeBiochemistryRow(row, 'value', newValue)}
                    inputWidth={`${BIOCHEMISTRY_VALUE_WIDTH}px`}
                    iconName='numbersIcon'
                  />
                  {/* A row is three fields, so it needs one visible control that removes all of them. The cross is
                      always laid out and only shown once the row is started, so every row's grey box keeps the
                      same width whether or not it can be removed. */}
                  <DlMuiDynamicIcon
                    iconName='haidiCloseIcon2'
                    title={t('filter.patient.biochemistryRemoveRow')}
                    useFixedViewBox={12}
                    noButtonStyles
                    css={muiStyles.biochemistryRemoveRow}
                    svgIconInputCss={muiStyles.biochemistryRemoveRowIcon}
                    onClick={() => removeBiochemistryRowFilter(row)}
                    style={isBiochemistryRowStarted(filters.patients, row) ? undefined : {visibility: 'hidden'}}
                    tabIndex={isBiochemistryRowStarted(filters.patients, row) ? undefined : -1}
                    aria-hidden={!isBiochemistryRowStarted(filters.patients, row)}
                  />
                </span>
              </div>
            )
        )}
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'biochemistryDate',
              ui: {label: 'filter.patient.biochemistryDate'},
            }}
            value={
              filters.patients.biochemistryFrom && filters.patients.biochemistryTo
                ? {
                    startDate: filters.patients.biochemistryFrom,
                    endDate: filters.patients.biochemistryTo,
                    shortcut: filters.patients.biochemistryFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  biochemistryFrom: newValue?.startDate,
                  biochemistryTo: newValue?.endDate,
                  biochemistryFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          {showBiochemistryMatch && (
            <LightSelectFilter
              config={{
                name: 'biochemistryMatch',
                ui: {label: 'filter.patient.biochemistryMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.biochemistryMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    biochemistryMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    microbiology: (
      <div css={muiStyles.tabPanel}>
        {MICROBIOLOGY_ROWS.map(
          (row, index) =>
            showMicrobiologyRow[index] && (
              <div css={muiStyles.conditionRowBox} key={row.pathogens}>
                <LightSelectFilterVirtualized
                  css={muiStyles.medicationsFilter}
                  config={{name: row.pathogens, ui: {label: row.pathogensLabel}}}
                  options={pathogenOptions}
                  multi
                  value={filters.patients[row.pathogens] ?? null}
                  onChange={(newValue) => changeMicrobiologyRow(row, 'pathogens', newValue)}
                />
                {/* The cross stays glued to the resistance field, like the biochemistry rows keep it by the value. */}
                <span css={muiStyles.biochemistryValueWithRemove}>
                  <LightSelectFilterVirtualized
                    css={muiStyles.medicationsFilter}
                    config={{name: row.resistances, ui: {label: row.resistancesLabel}}}
                    options={resistanceOptions}
                    multi
                    value={filters.patients[row.resistances] ?? null}
                    onChange={(newValue) => changeMicrobiologyRow(row, 'resistances', newValue)}
                  />
                  <DlMuiDynamicIcon
                    iconName='haidiCloseIcon2'
                    title={t('filter.patient.biochemistryRemoveRow')}
                    useFixedViewBox={12}
                    noButtonStyles
                    css={muiStyles.microbiologyRemoveRow}
                    svgIconInputCss={muiStyles.biochemistryRemoveRowIcon}
                    onClick={() => removeMicrobiologyRowFilter(row)}
                    style={isMicrobiologyRowStarted(filters.patients, row) ? undefined : {visibility: 'hidden'}}
                    tabIndex={isMicrobiologyRowStarted(filters.patients, row) ? undefined : -1}
                    aria-hidden={!isMicrobiologyRowStarted(filters.patients, row)}
                  />
                </span>
              </div>
            )
        )}
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'microbiologyDate',
              ui: {label: 'filter.patient.microbiologyDate'},
            }}
            value={
              filters.patients.microbiologyFrom && filters.patients.microbiologyTo
                ? {
                    startDate: filters.patients.microbiologyFrom,
                    endDate: filters.patients.microbiologyTo,
                    shortcut: filters.patients.microbiologyFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  microbiologyFrom: newValue?.startDate,
                  microbiologyTo: newValue?.endDate,
                  microbiologyFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          {showMicrobiologyMatch && (
            <LightSelectFilter
              config={{
                name: 'microbiologyMatch',
                ui: {label: 'filter.patient.microbiologyMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.microbiologyMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    microbiologyMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    // Other (PDM-2749): independent conditions, one line that wraps on a narrow screen. The diagnosis type
    // (admission / ongoing) joins the ICD field once the import carries it.
    other: (
      <div css={muiStyles.tabPanel}>
        <div css={muiStyles.medicationsFilterLine}>
          <LightSelectFilterVirtualized
            config={{name: 'icdCodes', ui: {label: 'filter.patient.icdCodes'}}}
            options={icdOptions}
            multi
            value={filters.patients.icdCodes ?? null}
            onChange={(newValue) => changeOther({icdCodes: newValue ?? undefined})}
          />
          <LightNumberRangeFilter
            config={{name: 'ageAtEnd', ui: {label: 'filter.patient.age', ...ageRange}}}
            value={numberRange(filters.patients.ageFrom, filters.patients.ageTo)}
            onChange={(newValue) => changeOther({ageFrom: newValue?.start, ageTo: newValue?.end})}
          />
          <LightNumberRangeFilter
            config={{name: 'weight', ui: {label: 'filter.patient.weight', ...weightRange}}}
            value={numberRange(filters.patients.weightFrom, filters.patients.weightTo)}
            onChange={(newValue) => changeOther({weightFrom: newValue?.start, weightTo: newValue?.end})}
          />
          <LightNumberRangeFilter
            config={{name: 'bmi', ui: {label: 'filter.patient.bmi', ...bmiRange}}}
            value={numberRange(filters.patients.bmiFrom, filters.patients.bmiTo)}
            onChange={(newValue) => changeOther({bmiFrom: newValue?.start, bmiTo: newValue?.end})}
          />
          <LightSelectFilter
            config={{name: 'deathDuringHospitalizations', ui: {label: 'filter.patient.deathDuringHospitalization'}}}
            // Enum order, not alphabetical: "yes", "yes, in text only" read as a scale. The "-" (no record)
            // goes first like in the other filters, where it lands there by the alphabetical sort.
            options={sortBy(enumMap.DeathDuringHospitalizationEnum ?? [], (v) => v.name !== 'NO_RECORD')}
            multi
            value={filters.patients.deathDuringHospitalizations}
            onChange={(newValue) => changeOther({deathDuringHospitalizations: newValue ?? undefined})}
          />
        </div>
      </div>
    ),
  };
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={utils.TranslateUtils.translate(I18N.PAGE_TITLE, intl)} />
      <LightSplitLayout
        initSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].sizes}
        minSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].minSize}
        storageId={PATIENTS_LAYOUT_SPLIT_CONFIG[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.patientsOpen, false]}
        hidden={[false, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.patientsOpen}}
      >
        <FilterPanelMaximizeWrapper>
          <LightFilterPanel
            open={filters.patientsOpen}
            toggleOpen={() =>
              changeFilters({
                patientsOpen: !filters.patientsOpen,
              })
            }
            hasActiveFilter={hasFilters}
            scrollAreaRef={filtersRef}
            filterInputsRef={filterInputsRef}
            filterInputsMinWidth={BIOCHEMISTRY_ROW_WIDTH}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.patients,
              setFilter: (newFilter) => {
                changeFilters({
                  patients: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizeHospitalizedPatientsFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
            selectedFiltersCount={selectedFiltersCount.total}
            maximize={{maximized: filterPanelMaximized, setMaximized: setFilterPanelMaximized}}
            tabs={{
              tabs: filterTabs,
              activeTab: filters.patientsTab,
              onChange: (tabId, open) =>
                changeFilters({patientsTab: tabId as HospitalizationFilterTab, ...(open ? {patientsOpen: true} : {})}),
              panels: tabPanels,
            }}
          />
        </FilterPanelMaximizeWrapper>
        <TableMaximizeWrapper>
          <IconHeaderWithSearch iconName={'listPlusIcon'} label={'patient.list.header.title'} childrenWrapperStyle={{gap: 6}}>
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={() => {
                changeFilters({
                  patients: DEFAULT_PATIENTS_FILTERS,
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              hasSorting={!isEqual(filters.patientsSorting, DEFAULT_PATIENTS_SORTING)}
              handleResetSorting={() => {
                changeFilters({
                  patientsSorting: DEFAULT_PATIENTS_SORTING,
                });
              }}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
              xlsxExportDisabled={listBlocked}
              xlsxExportDisabledTooltip='filter.patient.biochemistryValueInvalid'
            />
            <MaximizeButton maximized={tableMaximized} toggleMaximized={tableToggleMaximized} />
            {warningDialog}
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            variant='big'
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
              padding: 'dense',
            }}
            cellLineHeight='2.2'
            useMaxHeight
            columns={columns}
            data={data ?? []}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  pageSize,
                  page: clampPageNumber(filters.patientsPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                patientsSorting: {
                  field: columnId,
                  direction,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.patientsSorting;
            })()}
            scrollStorageId='/HospitalizedPatientsLayout.scrollXY'
          />
        </TableMaximizeWrapper>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  /** Content of a grouped tab (medication, biochemistry): one line per row, stacked; takes the whole inputs row. */
  tabPanel: css`
    flex-basis: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    row-gap: 4px;
  `,
  /**
   * One biochemistry condition (method + operator + value + its remove cross) in a content-hugging grey box, so the
   * three fields read as one condition (design PDM-2750). Same box look as the former medication box.
   */
  conditionRowBox: css`
    padding: 1px 8px 4px 8px;
    border-radius: 4px;
    background: var(--dlThemeGray-50);
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 4px;
    max-width: 100%;
  `,
  /** One line of the medication box: ATC slots, active ingredient slots, or the shared date + occurrence. */
  medicationsFilterLine: css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 8px;
    /* Wrapping is expected on a narrow screen; without this the wrapped fields would touch. */
    row-gap: 4px;
  `,
  medicationsFilterJoiner: css`
    font-family: var(--dlFontFamilyNormal);
    font-size: 14px;
    color: #121619;
  `,
  /** Holds the value field and its remove cross together, so the cross never wraps away from its row. */
  biochemistryValueWithRemove: css`
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    column-gap: 2px;
  `,
  /** Removes the whole condition. Flat and small, like the crosses on the chips below. */
  biochemistryRemoveRow: css`
    padding: 2px;
    color: var(--dlThemeGray-500);
    &:hover {
      color: var(--dlTheme-700);
    }
  `,
  biochemistryRemoveRowIcon: css`
    font-size: 14px;
  `,
  /** The microbiology rows' cross: the same look, pushed out to where the biochemistry rows have theirs. */
  microbiologyRemoveRow: css`
    padding: 2px;
    margin-left: ${CONDITION_ROW_REMOVE_GAP}px;
    color: var(--dlThemeGray-500);
    &:hover {
      color: var(--dlTheme-700);
    }
  `,
  medicationsFilter: css`
    background: #fff;
    border-radius: 4px;
  `,
});
